import React, { useRef } from 'react';
import classnames from 'classnames';
import Proptypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Avatar, Card, CardFooter, CardHeader, Text, Tooltip, makeStyles, tokens, typographyStyles } from '@fluentui/react-components';
import { CheckmarkCircle16Filled } from '@fluentui/react-icons';
import { ICON_FILL_COLOR } from './utils/constants';

const useStyles = makeStyles({
  cardContainer: {
    width: '358px',
  },
  cardTitle: {
    ...typographyStyles.body1Strong,
    color: '#242424',
  },
  cardSubtitle: {
    ...typographyStyles.caption1,
    color: '#616161',
  },
  cardFooter: {
    marginTop: '36px',
  },
  advertiserLinkContainer: {
    width: '93px',
    height: '32px',
    color: '#242424 !important',
    padding: `5px 0 ${tokens.spacingVerticalXXS} 0`,
    border: '1px solid #D1D1D1',
    borderRadius: '4px',
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0em',
    textAlign: 'center',
  },
  tooltip: {
    marginLeft: '5px',
    position: 'relative',
    top: '2px',
  },
});

// eslint-disable-next-line react/prop-types
const Header = ({
  name, isVerified, footerLinkRef,
}) => {
  const classes = useStyles();
  const iconRef = useRef(null);
  const tooltipLinkRef = useRef(null);
  const tooltipId = `tooltip-${name.replace(/\s+/g, '-').toLowerCase()}`;
  const isTooltipVisibleRef = useRef(false);

  const handleIconKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      if (isTooltipVisibleRef.current && tooltipLinkRef.current) {
        tooltipLinkRef.current.focus();
      } else if (!isTooltipVisibleRef.current && footerLinkRef && footerLinkRef.current) {
        footerLinkRef.current.focus();
      }
    }
  };

  const handleTooltipLinkKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      if (footerLinkRef && footerLinkRef.current) {
        footerLinkRef.current.focus();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      if (iconRef && iconRef.current) {
        iconRef.current.focus();
      }
    }
  };

  // Make it navigate to the icon instead of the non-rendered tooltip link
  const handleFooterLinkKeyDown = (e) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      if (iconRef && iconRef.current) {
        iconRef.current.focus();
      }
    }
  };

  const tooltipContent = (
    <div style={{ padding: '8px 12px', maxWidth: '300px' }}>
      Learn more about Microsoft&apos;s Advertiser Identity Verification Program{' '}
      <a
        href="https://help.ads.microsoft.com/#apex/ads/en/60151/-1"
        target="_blank"
        rel="noopener noreferrer"
        ref={tooltipLinkRef}
        onKeyDown={handleTooltipLinkKeyDown}
      >
        here
      </a>
    </div>
  );

  if (footerLinkRef && footerLinkRef.current) {
    // eslint-disable-next-line no-param-reassign
    footerLinkRef.current.onkeydown = handleFooterLinkKeyDown;
  }

  return (
    <>
      <Text className={classes.cardTitle}>
        {name}
      </Text>
      {isVerified && (
        <span className={classes.tooltip}>
          <Tooltip
            content={{
              children: tooltipContent,
              id: tooltipId,
            }}
            positioning="above-start"
            withArrow
            relationship="description"
            showDelay={0}
            hideDelay={500}
            onVisibleChange={(_ev, data) => {
              isTooltipVisibleRef.current = data.visible;
            }}
          >
            <span
              ref={iconRef}
              role="button"
              tabIndex={0}
              aria-label="Tooltip"
              aria-describedby={tooltipId}
              onKeyDown={handleIconKeyDown}
            >
              <CheckmarkCircle16Filled
                primaryFill={ICON_FILL_COLOR}
                aria-hidden="true"
              />
            </span>
          </Tooltip>
        </span>
      )}
    </>
  );
};

Header.propTypes = {
  name: Proptypes.string.isRequired,
  isVerified: Proptypes.bool,
  footerLinkRef: Proptypes.shape({
    current: Proptypes.shape({
      focus: Proptypes.func,
      onkeydown: Proptypes.func,
    }),
  }),
};

Header.defaultProps = {
  isVerified: false,
  footerLinkRef: null,
};

const AdvertiserResultCardComponent = (props) => {
  const {
    AdvertiserId: id,
    AdvertiserName: name,
    AdvertiserCountry: country,
    IsVerified: isVerified,
    searchConfig,
  } = props;
  const classes = useStyles();
  const updatedSearchConfig = { ...searchConfig, t: 'advertisers' };
  const footerLinkRef = useRef(null);

  return (
    <Card className={classes.cardContainer}>
      <CardHeader
        image={<Avatar name={name} shape="square" color="colorful" size={48} />}
        header={<Header name={name} isVerified={isVerified} footerLinkRef={footerLinkRef} />}
        description={<Text className={classes.cardSubtitle}>{`Advertiser | ${country}`}</Text>}
      />
      <CardFooter className={classes.cardFooter}>
        <Link
          className={classnames('advertiser-link-container', classes.advertiserLinkContainer)}
          to={`/advertiser/${id}`}
          state={updatedSearchConfig}
          aria-describedby="see all ads"
          aria-label={`see all ads for ${name}`}
          ref={footerLinkRef}
        >
          See all ads
        </Link>
      </CardFooter>
    </Card>
  );
};

AdvertiserResultCardComponent.propTypes = {
  AdvertiserId: Proptypes.string.isRequired,
  AdvertiserName: Proptypes.string.isRequired,
  AdvertiserCountry: Proptypes.string.isRequired,
  IsVerified: Proptypes.bool,
  searchConfig: Proptypes.shape({}),
};

AdvertiserResultCardComponent.defaultProps = {
  IsVerified: false,
  searchConfig: null,
};

export const AdvertiserResultCard = AdvertiserResultCardComponent;
